import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import {
  Autocomplete,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import * as ids from "common/ids";
import Component from "components/Component";
import Button from "components/common/Button";
import Disabled from "components/common/Disabled";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import ConstructionRecommendPanel from "components/inputs/glExposures/ConstructionRecommendPanel";
import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as staticDataService from "services/staticDataService";
import * as glExposuresStore from "store/glExposures";
import * as userSelectors from "store/selectors/user/userSelectors";

const makeBlankRow = () => ({
  id: ids.getUniqueKey(),
  state: null,
  classCode: null,
  exposureAmount: null,
  notes: null,
});

const HEADER_SX = {
  border: "none",
  color: "var(--mui-brand-gray-muted)",
  textTransform: "capitalize",
  padding: "6px",
  fontWeight: 500,
};

const BODY_SX = {
  border: "none",
  padding: "6px",
};

const SELECT_PROPS = {
  hiddenLabel: true,
  size: "small",
  variant: "filled",
  fullWidth: true,
};

const GlExposures = () => {
  const dispatch = useDispatch();
  const exposures = useSelector(glExposuresStore.selectExposures);
  const isReadOnly = useSelector(userSelectors.isUserReadOnly);

  const { data: glConfig } = staticDataService.useConfigQuery("gl_exposures");
  const states = useMemo(() => glConfig?.states ?? [], [glConfig]);
  const classCodes = useMemo(() => glConfig?.classCodes ?? [], [glConfig]);

  const classCodeIndex = useMemo(
    () => Object.fromEntries(classCodes.map((c) => [c.code, c])),
    [classCodes]
  );

  const writeExposures = useCallback(
    (next) => dispatch(glExposuresStore.update({ exposures: next })),
    [dispatch]
  );

  const updateRow = (id, patch) => {
    writeExposures(
      exposures.map((r) => (r.id === id ? { ...r, ...patch } : r))
    );
  };

  const removeRow = (id) =>
    writeExposures(exposures.filter((r) => r.id !== id));

  const addRow = () => writeExposures([...exposures, makeBlankRow()]);

  return (
    <Component title={"GL Exposures"}>
      <Stack spacing={2}>
        <ConstructionRecommendPanel />
        <TableContainer>
          <Table size={"small"} aria-label={"GL exposures"}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...HEADER_SX, minWidth: 100 }}>
                  {"State"}
                </TableCell>
                <TableCell sx={{ ...HEADER_SX, minWidth: 220 }}>
                  {"Class Code"}
                </TableCell>
                <TableCell sx={HEADER_SX}>{"Description"}</TableCell>
                <TableCell sx={HEADER_SX}>{"Exposure Base"}</TableCell>
                <TableCell sx={{ ...HEADER_SX, minWidth: 160 }} align={"right"}>
                  {"Exposure Amount"}
                </TableCell>
                <TableCell sx={{ ...HEADER_SX, minWidth: 220 }}>
                  {"Notes"}
                </TableCell>
                <TableCell sx={{ ...HEADER_SX, width: 40 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {exposures.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={BODY_SX}>
                    <Typography variant={"body2"} color={"text.secondary"}>
                      {"No exposure records yet. Add a row to start."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                exposures.map((row) => {
                  const classCode = row.classCode
                    ? classCodeIndex[row.classCode] ?? null
                    : null;
                  return (
                    <TableRow key={row.id}>
                      <TableCell sx={BODY_SX}>
                        <TextField
                          select
                          {...SELECT_PROPS}
                          value={row.state ?? ""}
                          disabled={isReadOnly}
                          onChange={(e) =>
                            updateRow(row.id, { state: e.target.value || null })
                          }
                          inputProps={{ "aria-label": "State" }}
                        >
                          {states.map((s) => (
                            <MenuItem key={s.code} value={s.code}>
                              {s.label ?? s.code}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell sx={BODY_SX}>
                        <Autocomplete
                          size={"small"}
                          fullWidth
                          options={classCodes}
                          disabled={isReadOnly}
                          value={classCodeIndex[row.classCode] ?? null}
                          getOptionLabel={(c) =>
                            `${c.code}${c.label ? ` - ${c.label}` : ""}`
                          }
                          filterOptions={(options, { inputValue }) => {
                            const q = inputValue.toLowerCase();
                            return options.filter(
                              (c) =>
                                c.code.toLowerCase().includes(q) ||
                                (c.label ?? "").toLowerCase().includes(q)
                            );
                          }}
                          onChange={(_, c) =>
                            updateRow(row.id, { classCode: c?.code ?? null })
                          }
                          renderOption={(props, c) => (
                            <li {...props} key={c.code}>
                              <Stack>
                                <Typography variant={"body2"}>
                                  {c.code}
                                  {c.label ? ` - ${c.label}` : ""}
                                </Typography>
                                {c.exposureBaseLabel && (
                                  <Typography
                                    variant={"caption"}
                                    color={"text.secondary"}
                                  >
                                    {c.exposureBaseLabel}
                                  </Typography>
                                )}
                              </Stack>
                            </li>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              hiddenLabel
                              variant={"filled"}
                              inputProps={{
                                ...params.inputProps,
                                "aria-label": "Class code",
                              }}
                            />
                          )}
                        />
                      </TableCell>
                      <TableCell sx={BODY_SX}>
                        <Typography variant={"subtitle1"}>
                          {classCode?.label ?? "-"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={BODY_SX}>
                        <Typography variant={"subtitle2"}>
                          {classCode?.exposureBaseLabel ?? "-"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={BODY_SX} align={"right"}>
                        <PrettyNumberTextField
                          type={"text"}
                          hiddenLabel
                          size={"small"}
                          variant={"filled"}
                          fullWidth
                          inputProps={{
                            style: { textAlign: "right" },
                            "aria-label": "Exposure amount",
                          }}
                          value={row.exposureAmount}
                          disabled={isReadOnly}
                          onChangeNumber={(value) =>
                            updateRow(row.id, { exposureAmount: value })
                          }
                        />
                      </TableCell>
                      <TableCell sx={BODY_SX}>
                        <TextField
                          {...SELECT_PROPS}
                          multiline
                          maxRows={3}
                          value={row.notes ?? ""}
                          disabled={isReadOnly}
                          onChange={(e) =>
                            updateRow(row.id, { notes: e.target.value || null })
                          }
                          inputProps={{ "aria-label": "Notes" }}
                        />
                      </TableCell>
                      <TableCell sx={BODY_SX}>
                        <IconButton
                          size={"small"}
                          aria-label={"Delete row"}
                          disabled={isReadOnly}
                          onClick={() => removeRow(row.id)}
                        >
                          <DeleteIcon fontSize={"small"} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Stack direction={"row"}>
          <Disabled ifReadOnly>
            <Button startIcon={<AddIcon />} onClick={addRow}>
              {"Add row"}
            </Button>
          </Disabled>
        </Stack>
      </Stack>
    </Component>
  );
};

export default GlExposures;
